import { ElementType } from 'react';
import CheckboxTile from '@ramsey-design-system/checkbox-tile';
import { useWizard } from 'react-use-wizard';
import { FormState, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { matcherGenericCheckboxSchema } from '../schemas/MatcherGenericCheckboxSchema';
import { getMatcherFormName, useTrackMatcherStepStarted } from '../utils';
import { useMatcherContext } from '../context';
import {
  getAnsweredValue,
  MatcherCheckboxOption,
  QuestionId
} from '../flow/types';
import {
  trackStepCompleted,
  trackStepErrorViewed
} from '../../../shared/analytics';
import { MatcherStandardStepSubtitleText } from './MatcherStandardStepSubtitleText';
import { MatcherStandardStepTitle } from './MatcherStandardStepTitle';
import { MatcherStepWrapper } from './MatcherStepWrapper';
import { MatcherStandardNextButton } from './MatcherStandardNextButton';
import { MatcherErrorText } from './MatcherErrorText';

interface MatcherGenericCheckboxStepFields {
  [key: string]: boolean | undefined;
}

interface MatcherGenericCheckboxStepProps {
  questionId: QuestionId;
  title: string;
  subtitle?: string;
  options: MatcherCheckboxOption[];
  stepName: string;
  showSkip?: boolean;
  // When false, nothing selected is not a validation error - submitting with no options
  // checked behaves the same as clicking Skip, instead of blocking with an error message.
  requireSelection?: boolean;
  nextButtonText?: string;
  nextButtonIcon?: ElementType;
  nextButtonIconPosition?: 'left' | 'right';
}

function getErrorText(formState: FormState<MatcherGenericCheckboxStepFields>) {
  return formState.errors['']?.message;
}

function getSelectedIds(
  values: MatcherGenericCheckboxStepFields,
  options: MatcherCheckboxOption[]
) {
  return options
    .filter((option) => values[option.id])
    .map((option) => option.id);
}

function getDefaultCheckedState(
  options: MatcherCheckboxOption[],
  answeredValue: string | string[] | undefined
): MatcherGenericCheckboxStepFields {
  const selectedIds = Array.isArray(answeredValue) ? answeredValue : [];
  return options.reduce((acc, option) => {
    acc[option.id] = selectedIds.includes(option.id);
    return acc;
  }, {} as MatcherGenericCheckboxStepFields);
}

function MatcherGenericCheckboxStep({
  questionId,
  title,
  subtitle,
  options,
  stepName,
  showSkip = true,
  requireSelection = true,
  nextButtonText,
  nextButtonIcon,
  nextButtonIconPosition
}: Readonly<MatcherGenericCheckboxStepProps>) {
  const { nextStep, activeStep } = useWizard();
  const { matcherAnswers, updateMatcherAnswer } = useMatcherContext();

  const { register, handleSubmit, formState, trigger } =
    useForm<MatcherGenericCheckboxStepFields>({
      defaultValues: getDefaultCheckedState(
        options,
        getAnsweredValue(matcherAnswers[questionId])
      ),
      resolver: requireSelection
        ? yupResolver(matcherGenericCheckboxSchema)
        : undefined
    });
  const errorText = getErrorText(formState);

  useTrackMatcherStepStarted(activeStep + 1, stepName, title);

  async function onValidSubmit(values: MatcherGenericCheckboxStepFields) {
    const selectedIds = getSelectedIds(values, options);
    if (!requireSelection && selectedIds.length === 0) {
      await onSkip();
      return;
    }

    updateMatcherAnswer(questionId, { status: 'answered', value: selectedIds });

    trackStepCompleted({
      step: activeStep + 1,
      step_name: stepName,
      step_label: title,
      answer_labels: options.map((option) => option.label),
      answer_values: selectedIds,
      form_name: getMatcherFormName()
    });
    await nextStep();
  }

  function onInvalidSubmit(
    errors: FormState<MatcherGenericCheckboxStepFields>['errors']
  ) {
    trackStepErrorViewed({
      step: activeStep + 1,
      step_name: stepName,
      step_label: title,
      error_type: 'validation',
      error_message: errors['']?.message ?? '',
      form_name: getMatcherFormName()
    });
  }

  async function onSkip() {
    updateMatcherAnswer(questionId, { status: 'skipped' });
    trackStepCompleted({
      step: activeStep + 1,
      step_name: stepName,
      step_label: title,
      answer_labels: options.map((option) => option.label),
      answer_values: ['skip'],
      form_name: getMatcherFormName()
    });
    await nextStep();
  }

  return (
    <MatcherStepWrapper onSkip={showSkip ? onSkip : undefined}>
      <form
        onSubmit={handleSubmit(onValidSubmit, onInvalidSubmit)}
        className="flex-1 flex flex-col"
      >
        <MatcherStandardStepTitle noSubtitle={!subtitle}>
          {title}
        </MatcherStandardStepTitle>
        {subtitle && (
          <MatcherStandardStepSubtitleText>
            {subtitle}
          </MatcherStandardStepSubtitleText>
        )}
        <div className="mb-4 space-y-1">
          {options.map((option) => {
            // Yup's atLeastOneRequired test is object-level, keyed to '' rather than any one
            // option's field name - RHF's default onChange re-validation only re-checks the
            // field that changed, so it never clears that '' error on its own. Forcing a full
            // re-trigger on every toggle keeps the error in sync with the current selection.
            // Gated on requireSelection (no resolver otherwise, so there's nothing to validate)
            // and on isSubmitted (so toggling a box before ever trying to continue doesn't
            // surface the error prematurely).
            const { onChange, ...fieldProps } = register(option.id);
            return (
              <CheckboxTile
                key={option.id}
                {...fieldProps}
                onChange={(e) => {
                  onChange(e);
                  if (requireSelection && formState.isSubmitted) trigger();
                }}
                label={option.label}
                data-testid={`matcher_${option.id}_checkbox`}
                id={option.id}
                size="sm"
              />
            );
          })}
          <MatcherErrorText errorText={errorText} />
        </div>

        <div className="mt-auto md:mt-0">
          <MatcherStandardNextButton
            buttonText={nextButtonText}
            icon={nextButtonIcon}
            iconPosition={nextButtonIconPosition}
          />
        </div>
      </form>
    </MatcherStepWrapper>
  );
}

export { MatcherGenericCheckboxStep };
